import {
  AlertTriangle,
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Car,
  Euro,
  FilePen,
  FileText,
  FileUp,
  Globe,
  Hash,
  Mail,
  MapPin,
  PartyPopper,
  Phone,
  Shield,
  ShieldAlert,
  ShieldCheck,
  TruckIcon,
  User,
  Users,
} from 'lucide-react';
import type {
  ConditionalVisibility,
  FieldShortcut,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { ContactKey } from '@folio/utils/customerDisplayName';
import {
  customerControllerEditCustomer,
  CustomFieldDto,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import { InvoicesScheduleModule, QuoteFunnelModule } from '@folio/modules';
import { TUTASSUR_RECAP_SHORT_LABELS } from './common';

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const signatorySections: FormSectionsWithShortcuts = [
  {
    title: 'Signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      {
        name: 'contactFirstName',
        type: 'text',
        required: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactLastName',
        type: 'text',
        required: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactAddress',
        type: 'address',
        required: true,
        isCustomField: true,
        icon: MapPin,
      },
      {
        name: 'contactPostCode',
        type: 'postcode',
        countries: ['FR'],
        required: true,
        isCustomField: true,
        icon: Hash,
        colSpan: 4 as const,
      },
      {
        name: 'contactCity',
        type: 'city',
        required: true,
        isCustomField: true,
        icon: Building2,
        colSpan: 8 as const,
      },
      {
        name: 'contactEmail',
        type: 'email',
        required: true,
        isCustomField: true,
        icon: Mail,
        colSpan: 6 as const,
      },
      {
        name: 'contactPhoneNumber',
        type: 'text',
        required: true,
        isCustomField: true,
        icon: Phone,
        colSpan: 6 as const,
      },
      {
        name: 'contactBirthDate',
        type: 'date',
        required: true,
        isCustomField: true,
        icon: CalendarDays,
        colSpan: 6 as const,
      },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        required: true,
        isCustomField: true,
        icon: Building2,
        colSpan: 6 as const,
      },
      {
        name: 'contactBirthCountry',
        type: 'country',
        addressGroup: 'contactBirth',
        required: true,
        isCustomField: true,
        icon: Globe,
      },
    ],
  },
];

const financialCompanySections: FormSectionsWithShortcuts = [
  {
    title: "Informations financières de l'année dernière ou projection",
    fields: [
      { name: 'lastExerciseOrForecastRevenue', icon: Euro, colSpan: 6 as const },
      { name: 'yearOfRevenue', icon: CalendarDays, colSpan: 6 as const },
    ],
  },
];

const companySections: FormSectionsWithShortcuts = [
  {
    title: 'Entreprise',
    fields: [
      {
        name: 'isCompanyBeingCreated',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData =>
          apiData.customFields?.isCompanyBeingCreated ? 'true' : 'false',
        formDataAccessor: formData => formData.isCompanyBeingCreated === 'true',
      },
      {
        name: 'companySiret',
        type: 'company',
        required: true,
        isCustomField: true,
        displayCompanyInformation: true,
        icon: Building2,
        // Consider making it more generic, this should be reusable.
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                },
                nafLabel: apiData?.customFields?.companyNafLabel,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        showWhen: [
          {
            fieldName: 'isCompanyBeingCreated',
            condition: 'equals',
            value: 'false',
          },
        ],
      },
      {
        name: 'companyName',
        required: true,
        type: 'text',
        icon: Building2,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterAddress',
        required: true,
        type: 'address',
        icon: MapPin,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        required: true,
        type: 'postcode',
        countries: ['FR'],
        icon: Hash,
        colSpan: 4 as const,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCity',
        required: true,
        type: 'city',
        icon: Building2,
        colSpan: 8 as const,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyLegalStatus1',
        required: true,
        type: 'text',
        icon: FileText,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCreationDate',
        required: true,
        type: 'date',
        icon: CalendarDays,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyActivity',
        required: true,
        type: 'text',
        icon: BriefcaseIcon,
        isCustomField: true,
      },
    ],
  },
  ...financialCompanySections,
];

const beneficialOwnersSections: FormSectionsWithShortcuts = [
  {
    title: 'Nombre de bénéficiaires effectifs',
    fields: [
      {
        name: 'numberOfBeneficialOwners',
        type: 'number' as const,
        display: 'cards' as const,
        required: true,
        isCustomField: true,
        min: 0,
        max: 5,
        icon: Users,
        ignoreInPayload: true,
        apiDataAccessor: customer =>
          Object.entries(customer.customFields ?? {}).filter(
            ([key, value]) => key.startsWith('beneficialOwnerFirstName') && Boolean(value),
          ).length,
      },
    ],
  },
  ...Array.from({ length: 5 }, (_, index) => ({
    title: `Bénéficiaire effectif ${index + 1}`,
    fields: [
      {
        name: `beneficialOwnerFirstName${index + 1}`,
        type: 'text' as const,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Michel',
      },
      {
        name: `beneficialOwnerLastName${index + 1}`,
        type: 'text' as const,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: User,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Dupont',
      },
      {
        name: `beneficialOwnerDateOfBirth${index + 1}`,
        type: 'date' as const,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: CalendarDays,
      },
      {
        name: `beneficialOwnerBirthCity${index + 1}`,
        type: 'city' as const,
        addressGroup: `beneficialOwnerBirth${index + 1}`,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: Building2,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Paris',
      },
      {
        name: `beneficialOwnerBirthCountry${index + 1}`,
        type: 'country' as const,
        addressGroup: `beneficialOwnerBirth${index + 1}`,
        required: true,
        clearable: true,
        isCustomField: true,
        icon: Globe,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'France',
      },
    ],
    showWhen: [
      {
        fieldName: 'numberOfBeneficialOwners',
        condition: 'greaterThanOrEquals' as const,
        value: index + 1,
      },
    ],
  })),
];

const questionsSections: FormSectionsWithShortcuts = [
  {
    title: 'Informations complémentaires',
    fields: [
      {
        name: 'specificQuestionEPrive1',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Shield,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionEPrive1 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionEPrive1 }) => specificQuestionEPrive1 === 'true',
      },
      {
        name: 'specificQuestionEPrive2',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldCheck,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionEPrive2 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionEPrive2 }) => specificQuestionEPrive2 === 'true',
      },
      {
        name: 'specificQuestionEPrive3',
        description:
          "France continentale, Corse, Espagne, Principautés d'Andorre et de Monaco, Italie, Suisse, Allemagne, Grand Duché du Luxembourg, Belgique, Grande Bretagne",
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Globe,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionEPrive3 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionEPrive3 }) => specificQuestionEPrive3 === 'true',
      },
      {
        name: 'specificQuestionEPrive4',
        description:
          "Animaux vivants, Bijoux et pièces d'orfèvrerie, Billets de banque, Coupons, Engins de chantier, Espèces monnayées, Fourrures et pelleteries, Instruments de musique, Marchandises ou matières classées dangereuses par la réglementation, Marchandises transportées sous température dirigée, en citerne, en vrac, Matériels de travaux publics et agricoles, Métaux et pierres précieux, Objets et oeuvres d'art, Perles, Tabac, Titres, Valeurs de toutes natures, Véhicules.",
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldAlert,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionEPrive4 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionEPrive4 }) => specificQuestionEPrive4 === 'true',
      },
      {
        name: 'specificQuestionEPrive5',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: AlertTriangle,
        isCustomField: true,
        apiDataAccessor: ({ customFields }) =>
          customFields?.specificQuestionEPrive5 ? 'true' : 'false',
        formDataAccessor: ({ specificQuestionEPrive5 }) => specificQuestionEPrive5 === 'true',
      },
    ],
  },
  ...Array.from({ length: 10 }, (_, index) => ({
    title: `Véhicule ${index + 1}`,
    display: 'row' as const,
    showWhen: [
      {
        fieldName: 'specificQuestionEPrive1',
        condition: 'equals' as const,
        value: 'false',
      },
    ],
    fields: [
      {
        name: `registrationNumber${index + 1}`,
        type: 'text' as const,
        required: index === 0,
        isCustomField: true,
        clearable: true,
        icon: Car,
        DEPRECATED_placeholder: 'AA-000-BB',
      },
      {
        name: `insuredAmount${index + 1}`,
        type: 'text' as const,
        required: index === 0,
        isCustomField: true,
        clearable: true,
        icon: Euro,
        DEPRECATED_placeholder: '50000',
      },
    ],
  })),
];

const assetFields: FieldShortcut[] = [
  {
    name: 'startedAt',
    required: true,
    type: 'datetime',
    min: 'now',
  },
];

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

const manualComponentsGroups = [
  {
    id: 'premium',
    componentIds: ['PREMIUM', 'WHOLESALE_COMMISSION', 'INSURANCE_TAX'],
    placeholders: {
      PREMIUM: '1000',
      WHOLESALE_COMMISSION: '100',
      INSURANCE_TAX: '150',
    } as Record<string, string>,
  },
  {
    id: 'wholesaleFees',
    componentIds: ['WHOLESALE_ADDITIONAL_COMMISSION', 'WHOLESALE_SUBSCRIPTION_FEE'],
    placeholders: {
      WHOLESALE_ADDITIONAL_COMMISSION: '10',
      WHOLESALE_SUBSCRIPTION_FEE: '50',
    } as Record<string, string>,
  },
  {
    id: 'retailFees',
    componentIds: ['RETAIL_COMMISSION', 'RETAIL_SUBSCRIPTION_FEE', 'RETAIL_MANAGEMENT_FEE'],
    placeholders: {
      RETAIL_COMMISSION: '10',
      RETAIL_SUBSCRIPTION_FEE: '50',
      RETAIL_MANAGEMENT_FEE: '30',
    } as Record<string, string>,
  },
  {
    id: 'otherFees',
    componentIds: ['RENEWAL_FEE'],
    placeholders: {
      RENEWAL_FEE: '30',
    } as Record<string, string>,
  },
];

const productCustomPath = '/transport-marchandises-e-prive';

const policyHolderInvitationRole = ['DRIVER'];

export const tutassurTransportMarchandisesEPriveConfig = {
  display: {
    category: 'transport',
    icon: TruckIcon,
    badges: ['custom'],
  },
  productCustomPath,
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        // Needed so an asset exists for the quote step, so we can fill the startedAt field
        // Needs to be first to avoid issues when clicking "previous" from customer step
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'company',
        type: 'customerFunnel',
        sections: companySections,
        icon: Building2,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: signatorySections,
        icon: User,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'questions',
        type: 'policyFunnel',
        sections: questionsSections,
        icon: ShieldAlert,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            assetFields={assetFields}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'beneficial-owners',
        type: 'customerFunnel',
        sections: beneficialOwnersSections,
        icon: Users,
        afterSubmit: async (data, { customerId, branchId }) => {
          const numberOfBeneficialOwners = Number(data.numberOfBeneficialOwners);

          const fieldsToUpdate: CustomFieldDto[] = Array.from(
            { length: 5 - numberOfBeneficialOwners },
            (_, index) => [
              `beneficialOwnerFirstName${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerLastName${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerDateOfBirth${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerBirthCity${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerBirthCountry${numberOfBeneficialOwners + index + 1}`,
            ],
          )
            .flat()
            .map(key => ({ key, value: '' }));

          if (fieldsToUpdate.length > 0) {
            await customerControllerEditCustomer(
              customerId,
              { customFields: fieldsToUpdate },
              { branchId },
            );
          }
        },
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: true },
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },
  mtaFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'company',
        type: 'customerFunnel',
        sections: companySections,
        icon: Building2,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: signatorySections,
        icon: User,
      },
      {
        path: 'questions',
        type: 'policyFunnel',
        sections: questionsSections,
        icon: ShieldAlert,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
      },
    ],
  },
  policyholderRenewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: financialCompanySections,
        icon: User,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId }) => `/policyholder/${policyId}/information`,
      },
    ],
  },
  renewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: financialCompanySections,
        icon: User,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}`,
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: false,
    quoteDerogationFunnelModule: {
      afterEndPath: '/derogations',
      steps: [
        {
          path: 'quote',
          type: 'custom',
          component: (
            <QuoteFunnelModule
              nonDelegatedDocuments={['QUOTE']}
              manualComponentsGroups={manualComponentsGroups}
              recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
            />
          ),
        },
      ],
    },
  },
  priceBreakdownConfig: {
    groups: [
      {
        lines: [
          { key: 'TOTAL_PREMIUM', variant: 'dimmed' },
          { key: 'TOTAL_PREMIUM_RETAIL', variant: 'dimmed' },
        ],
      },
      { lines: [{ key: 'BROKERAGE_FEES' }, { key: 'BROKERAGE_FEES_RETAIL' }] },
      { lines: [{ key: 'MANAGEMENT_FEE' }] },
      {
        lines: [
          { key: 'SUBSCRIPTION_FEE' },
          { key: 'SUBSCRIPTION_FEE_RETAIL' },
          { key: 'RENEWAL_FEE', tooltip: true },
        ],
      },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      { key: 'contactCivility', type: 'text' },
      { key: 'contactFirstName', type: 'text', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', type: 'text', contactKey: ContactKey.LAST_NAME },
      { key: 'contactEmail', type: 'email' },
      { key: 'contactPhoneNumber', type: 'text' },
      { key: 'contactBirthDate', type: 'date' },
      { key: 'contactBirthCity', type: 'text' },
      { key: 'contactBirthCountry', type: 'text' },
      { key: 'lastExerciseOrForecastRevenue', type: 'number' },
      { key: 'yearOfRevenue', type: 'text' },
      { key: 'companyActivity', type: 'text' },
      { key: 'isCompanyBeingCreated', type: 'boolean' },
      { key: 'companySiret', type: 'text' },
      { key: 'companySiren', type: 'text' },
      {
        key: 'companyName',
        type: 'text',
        contactKey: ContactKey.COMPANY_NAME,
        forceReadonly: true,
      },
      { key: 'companyNafCode', type: 'text' },
      { key: 'companyNafLabel', type: 'text', forceReadonly: true },
      { key: 'companyCreationDate', type: 'date', forceReadonly: true },
      { key: 'companyLegalStatus1', type: 'text', forceReadonly: true },
      { key: 'companyLegalStatus2', type: 'text', forceReadonly: true },
      { key: 'companyLegalStatus3', type: 'text', forceReadonly: true },
      { key: 'companyHeadquarterAddress', type: 'text', forceReadonly: true },
      {
        key: 'companyHeadquarterPostcode',
        type: 'postcode',
        countries: ['FR'],
        forceReadonly: true,
      },
      { key: 'companyHeadquarterCity', type: 'text', forceReadonly: true },
      { key: 'companyHeadquarterCountry', type: 'text' },
      ...Array.from({ length: 5 }, (_, index) => index + 1).flatMap(index => [
        `beneficialOwnerFirstName${index}`,
        `beneficialOwnerLastName${index}`,
        `beneficialOwnerBirthCity${index}`,
        `beneficialOwnerBirthCountry${index}`,
      ]),
    ],
  },
  policyInformationCard: {
    title: 'Informations complémentaires',
    fieldsConfig: [
      {
        key: 'specificQuestionEPrive1',
        type: 'boolean',
      },
      {
        key: 'specificQuestionEPrive2',
        type: 'boolean',
      },
      {
        key: 'specificQuestionEPrive3',
        type: 'boolean',
      },
      {
        key: 'specificQuestionEPrive4',
        type: 'boolean',
      },
      {
        key: 'specificQuestionEPrive5',
        type: 'boolean',
      },
      ...Array.from({ length: 10 }, (_, index) => index + 1).flatMap(index => [
        {
          key: `registrationNumber${index}`,
          type: 'text' as const,
          hideIfEmpty: true,
          forceReadonly: true,
        },
        {
          key: `insuredAmount${index}`,
          type: 'text' as const,
          hideIfEmpty: true,
          forceReadonly: true,
        },
      ]),
    ],
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: false,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-supporting-validation',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'wholesaler-quote-uploaded',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ documents }) =>
          documents.some(({ type, category }) => type === 'non_delegated' && category === 'QUOTE'),
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  enableAssetComparison: false,
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/transport-marchandises-e-prive/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/transport-marchandises-e-prive/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: false,
    isCompareDisabled: true,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
} satisfies KorintModulesProductsConfig;
